export function canonicalHeight(value) {
  if (value === '' || value === null || value === undefined) return null;
  const height = Number(value);
  return Number.isInteger(height) && height >= 100 && height <= 230 ? height : null;
}

export function imperialHeight(feet, inches) {
  if (String(feet).trim() === '' || String(inches).trim() === '') return null;
  const ft = Number(feet), inch = Number(inches);
  if (!Number.isInteger(ft) || !Number.isInteger(inch) || ft < 3 || ft > 7 || inch < 0 || inch > 11) return null;
  return canonicalHeight(Math.round((ft * 12 + inch) * 2.54));
}

export function hasHeightGuide(product) {
  const min = Number(product.min), max = Number(product.max);
  return Number.isFinite(min) && Number.isFinite(max) && min >= 100 && max <= 230 && min <= max;
}

export function fitProducts(products, height, includeUnknown = false) {
  let unknown = 0;
  const matches = [];
  products.forEach((product, index) => {
    const known = hasHeightGuide(product);
    if (!known) unknown += 1;
    if ((known && height >= Number(product.min) && height <= Number(product.max)) || (!known && includeUnknown)) {
      matches.push({ ...product, sourceIndex: index, known });
    }
  });
  return { matches, unknown };
}

export function fitPage(matches, requestedPage, pageSize) {
  const pages = Math.max(1, Math.ceil(matches.length / pageSize));
  const page = Math.max(1, Math.min(pages, Math.trunc(Number(requestedPage)) || 1));
  const items = matches.slice((page - 1) * pageSize, page * pageSize);
  return { page, pages, items, sourcePages: [...new Set(items.map(item => Math.floor(item.sourceIndex / pageSize) + 1))] };
}

export function bikeTypeTarget(currentUrl, galleryUrl, type, categoryType = '__collection__') {
  const target = new URL(currentUrl), gallery = new URL(galleryUrl, target);
  if (gallery.origin !== target.origin || !gallery.pathname.includes('/collections/')) return target;
  const scoped = target.pathname !== gallery.pathname;
  const keepCategory = scoped && (type === '__collection__' || type === categoryType);
  target.searchParams.delete('filter.p.m.custom.bike_type');
  if (!keepCategory) {
    target.pathname = gallery.pathname;
    if (type && type !== '__collection__') target.searchParams.set('filter.p.m.custom.bike_type', type);
    target.searchParams.delete('view');
    if (scoped) target.searchParams.delete('q');
  }
  ['page', 'rider_fit_page', 'section_id'].forEach(key => target.searchParams.delete(key));
  return target;
}

function installRiderFit() {
  if (window.EbikeBuyerRiderFit) return;
  const HEIGHT = 'rider_height_cm', UNIT = 'rider_height_unit', UNKNOWN = 'rider_include_unknown', PAGE = 'rider_fit_page';
  const PREFERENCE = 'ebikebuyer.rider-fit.v1';
  const indexCache = new Map(), sectionCache = new Map();
  const dirtyWidgets = new WeakSet();
  let activeController = null, activeRequestKey = '', requestId = 0, preferredUnit = 'ft', unknownCount = 0;
  const root = () => document.querySelector('[data-ebike-rider-collection]');
  const widgets = () => document.querySelectorAll('[data-rider-fit]');
  const formFor = element => element?.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

  function labels() {
    const node = root()?.querySelector('[data-rider-fit-labels]');
    return node ? JSON.parse(node.textContent) : {};
  }

  function state(url = new URL(location.href)) {
    const height = canonicalHeight(url.searchParams.get(HEIGHT));
    const unit = url.searchParams.get(UNIT);
    return { height, unit: unit === 'cm' || unit === 'ft' ? unit : preferredUnit,
      unknown: url.searchParams.get(UNKNOWN) === '1', page: Number(url.searchParams.get(PAGE)) || 1 };
  }

  function readPreference() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(PREFERENCE));
      if (!saved || !['cm', 'ft'].includes(saved.unit)) return null;
      const height = canonicalHeight(saved.height);
      if (saved.height !== null && height === null) return null;
      return { height, unit: saved.unit, unknown: height !== null && saved.unknown === true };
    } catch {
      return null;
    }
  }

  function savePreference(current) {
    try {
      window.localStorage.setItem(PREFERENCE, JSON.stringify({
        height: current.height, unit: current.unit, unknown: Boolean(current.height && current.unknown)
      }));
    } catch {}
  }

  function clearPreference() {
    try {
      window.localStorage.removeItem(PREFERENCE);
    } catch {}
  }

  function supports(url) {
    const target = new URL(url, location.href);
    return Boolean(root()) && target.origin === location.origin && target.pathname === location.pathname;
  }

  function nativeUrl(url) {
    const next = new URL(url, location.href);
    [HEIGHT, UNIT, UNKNOWN, PAGE, 'view', 'page'].forEach(key => next.searchParams.delete(key));
    for (const [key, value] of [...next.searchParams]) if (value === '') next.searchParams.delete(key);
    next.searchParams.sort();
    return next;
  }

  function remember(cache, key, value, limit) {
    cache.set(key, { value, expires: Date.now() + 60000 });
    while (cache.size > limit) cache.delete(cache.keys().next().value);
  }

  function cached(cache, key) {
    const entry = cache.get(key);
    if (entry?.expires > Date.now()) return entry.value;
    cache.delete(key);
    return null;
  }

  async function requestText(url, signal) {
    const response = await fetch(url, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Rider fit request failed');
    return response.text();
  }

  async function sectionText(url, signal) {
    const key = url.toString();
    const found = cached(sectionCache, key);
    if (found !== null) return found;
    const text = await requestText(url, signal);
    if (!text.includes('ProductGridContainer')) throw new Error('Collection grid missing');
    remember(sectionCache, key, text, 40);
    return text;
  }

  async function collectionIndex(base, signal) {
    const url = new URL(base);
    url.searchParams.delete('section_id');
    url.searchParams.set('view', 'ebikebuyer-rider-fit-data');
    const key = url.toString();
    const found = cached(indexCache, key);
    if (found !== null) return found;
    const products = [];
    let pages = 1, total = 0;
    for (let page = 1; page <= pages; page += 1) {
      url.searchParams.set('page', page);
      const data = JSON.parse(await requestText(url, signal));
      if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 || data.page !== page) {
        throw new Error('Invalid collection index');
      }
      pages = Math.max(1, data.pages);
      total = data.total;
      products.push(...data.products);
    }
    if (products.length !== total || new Set(products.map(p => String(p.id))).size !== total) throw new Error('Incomplete collection index');
    remember(indexCache, key, products, 12);
    return products;
  }

  function makeButton(text, attribute, className = 'btn--link') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute(attribute, '');
    button.textContent = text;
    return button;
  }

  function heightText(current) {
    if (current.unit === 'ft') {
      const inches = Math.round(current.height / 2.54);
      return `${Math.floor(inches / 12)}′${inches % 12}″`;
    }
    return `${current.height}cm`;
  }

  function addActiveHeight(doc, current, copy) {
    const active = doc.getElementById('FacetFiltersActive');
    if (!active) return;
    const chip = makeButton(copy.height_chip.replace('__HEIGHT__', heightText(current)), 'data-rider-clear-height', 'facet-remove btn btn--secondary flex relative');
    chip.setAttribute('aria-label', copy.clear_height);
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 20 20');
    icon.setAttribute('width', '14'); icon.setAttribute('height', '14'); icon.setAttribute('aria-hidden', 'true');
    icon.style.marginInlineStart = '8px';
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm5 5 10 10M15 5 5 15'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2');
    icon.append(path); chip.append(icon); active.prepend(chip);
    if (active.querySelectorAll('.facet-remove').length > 1 && !active.querySelector('.facet-clear-all')) {
      active.append(makeButton(copy.clear_all, 'data-rider-clear-all', 'facet-clear-all btn--link'));
    }
    doc.querySelectorAll('facet-count').forEach(counter => {
      const count = Number(counter.textContent.replace(/\D/g, '')) || 0;
      counter.textContent = `(${count + 1})`;
      counter.hidden = false;
    });
  }

  function pagination(doc, current, selected, url, copy) {
    if (selected.pages <= 1) return null;
    const nav = doc.createElement('nav');
    nav.className = 'pagination'; nav.setAttribute('aria-label', copy.pagination);
    const list = doc.createElement('ul');
    list.className = 'pagination__list list-unstyled flex items-center flex-wrap justify-center';
    const numbers = new Set([1, selected.pages]);
    for (let n = Math.max(1, selected.page - 2); n <= Math.min(selected.pages, selected.page + 2); n += 1) numbers.add(n);
    const addLink = (page, text, className = '') => {
      const item = doc.createElement('li'), link = doc.createElement('a'), target = new URL(url);
      target.searchParams.delete('section_id'); target.searchParams.delete('page');
      if (page > 1) target.searchParams.set(PAGE, page); else target.searchParams.delete(PAGE);
      link.href = target.toString(); link.className = `pagination__item ${className}`.trim();
      link.textContent = text; link.setAttribute('data-rider-page', '');
      link.setAttribute('aria-label', copy.page.replace('__PAGE__', page));
      if (page === selected.page && /^\d+$/.test(text)) {
        link.classList.add('pagination__item--current', 'background-2'); link.setAttribute('aria-current', 'page');
      }
      item.append(link); list.append(item);
    };
    if (selected.page > 1) addLink(selected.page - 1, copy.previous, 'pagination__item--previous');
    let previous = 0;
    [...numbers].sort((a, b) => a - b).forEach(page => {
      if (previous && page - previous > 1) {
        const item = doc.createElement('li'), span = doc.createElement('span');
        span.className = 'pagination__item'; span.textContent = '…'; item.append(span); list.append(item);
      }
      addLink(page, String(page)); previous = page;
    });
    if (selected.page < selected.pages) addLink(selected.page + 1, copy.next, 'pagination__item--next');
    nav.append(list); return nav;
  }

  async function filteredResponse(url, current, signal) {
    const base = nativeUrl(url);
    const [firstText, products] = await Promise.all([sectionText(base, signal), collectionIndex(base, signal)]);
    const copy = labels(), doc = new DOMParser().parseFromString(firstText, 'text/html');
    const pageSize = Number(doc.querySelector('[data-ebike-rider-collection]')?.dataset.riderPageSize) || Number(root().dataset.riderPageSize) || 36;
    const { matches, unknown } = fitProducts(products, current.height, current.unknown);
    const selected = fitPage(matches, current.page, pageSize);
    const documents = new Map([[1, doc]]);
    const remaining = selected.sourcePages.filter(page => page !== 1);
    let nextPage = 0;
    const worker = async () => {
      while (nextPage < remaining.length) {
        const page = remaining[nextPage++], pageUrl = new URL(base);
        pageUrl.searchParams.set('page', page);
        const text = await sectionText(pageUrl, signal);
        documents.set(page, new DOMParser().parseFromString(text, 'text/html'));
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, remaining.length) }, worker));
    const grid = doc.getElementById('ProductsList'), container = doc.getElementById('ProductGridContainer');
    if (!grid || !container) throw new Error('Collection grid missing');
    const promotions = [...grid.children].map((node, index) => ({ node, index })).filter(item => item.node.classList.contains('image-column'));
    const cards = selected.items.map(item => {
      if (!/^\d+$/.test(String(item.id))) throw new Error('Invalid product ID');
      const page = Math.floor(item.sourceIndex / pageSize) + 1;
      const card = documents.get(page)?.querySelector(`[data-rider-product-id="${item.id}"]`);
      if (!card) throw new Error('Collection changed during filtering');
      if (!item.known) {
        const label = doc.createElement('span'); label.className = 'ebike-rider-fit__unconfirmed'; label.textContent = copy.unconfirmed; card.prepend(label);
      }
      return card;
    });
    grid.replaceChildren(...cards);
    if (cards.length) promotions.forEach(({ node, index }) => { if (index <= grid.children.length) grid.insertBefore(node, grid.children[index] || null); });
    container.querySelectorAll('.pagination,.empty-state,.ebike-rider-fit__notice').forEach(node => node.remove());
    if (!matches.length) {
      const empty = doc.createElement('div'); empty.className = 'empty-state flex flex-col items-center justify-center gap-6 text-center';
      const message = doc.createElement('p'); message.className = 'h4'; message.textContent = copy.no_matches;
      empty.append(message, makeButton(copy.clear_height, 'data-rider-clear-height', 'btn btn--primary')); container.prepend(empty);
    }
    if (unknown > 0) {
      const notice = doc.createElement('p'); notice.className = 'ebike-rider-fit__notice';
      const text = unknown === 1 ? copy.unknown_one : copy.unknown_many.replace('__COUNT__', unknown);
      notice.append(doc.createTextNode(text + ' '));
      notice.append(makeButton(current.unknown ? copy.exclude_unknown : copy.include_unknown_action, 'data-rider-toggle-unknown'));
      container.prepend(notice);
    }
    const nav = pagination(doc, current, selected, url, copy); if (nav) container.append(nav);
    const count = doc.getElementById('ProductCount');
    if (count) count.textContent = matches.length === 1 ? copy.count_one : copy.count_many.replace('__COUNT__', matches.length);
    addActiveHeight(doc, current, copy);
    const canonical = new URL(url); canonical.searchParams.delete('page');
    if (selected.page > 1) canonical.searchParams.set(PAGE, selected.page); else canonical.searchParams.delete(PAGE);
    return { text: doc.documentElement.outerHTML, url: canonical, unknown };
  }

  function applyResponse(form, text, event) {
    form.renderFilters(text, event, true); form.renderFiltersActive(text); form.renderProductGridContainer(text);
    form.renderProductCount(text); form.renderSortBy(text); form.renderSortByMobile(text);
    window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: text });
    form.afterRenderSection();
  }

  function showLoading(loading) {
    widgets().forEach(widget => {
      const button = widget.querySelector('[data-rider-apply]'), text = button?.querySelector('.btn__text');
      if (!button || !text) return;
      if (loading) {
        if (!button.hasAttribute('data-rider-idle-label')) button.setAttribute('data-rider-idle-label', text.textContent);
        button.setAttribute('aria-busy', 'true');
        text.textContent = labels().loading;
      } else {
        if (button.hasAttribute('data-rider-idle-label')) text.textContent = button.getAttribute('data-rider-idle-label');
        button.removeAttribute('data-rider-idle-label'); button.removeAttribute('aria-busy');
      }
    });
  }

  function render(url, form, event) {
    if (!supports(url)) return false;
    const target = new URL(url, location.href), current = state(target), key = new URL(target);
    key.searchParams.sort();
    if (activeController && !activeController.signal.aborted && activeRequestKey === key.toString()) return true;
    activeController?.abort();
    const controller = new AbortController(), id = ++requestId;
    activeController = controller; activeRequestKey = key.toString();
    if (current.height) savePreference(current);
    document.documentElement.classList.toggle('ebike-rider-fit-active', Boolean(current.height));
    form.beforeRenderSection();
    showLoading(true);
    const container = document.getElementById('ProductGridContainer'); container?.setAttribute('aria-busy', 'true');
    const count = document.getElementById('ProductCount'); if (count) count.textContent = current.height ? labels().loading : labels().updating;
    if (form.hasAttribute('data-history')) form.updateURLHash(target);
    syncWidgets();
    const run = async () => {
      const result = current.height ? await filteredResponse(target, current, controller.signal) : { text: await sectionText(target, controller.signal), url: target, unknown: 0 };
      if (id !== requestId || controller.signal.aborted) return;
      unknownCount = result.unknown;
      if (form.hasAttribute('data-history')) form.updateURLHash(result.url);
      applyResponse(form, result.text, event); syncWidgets();
      if (event?.target?.closest('[data-rider-fit]')) {
        requestAnimationFrame(() => {
          const sidebar = document.querySelector('#FacetFiltersContainer > .sticky-element');
          if (sidebar) sidebar.scrollTop = 0;
        });
      }
      document.getElementById('ProductGridContainer')?.removeAttribute('aria-busy');
    };
    run().catch(error => {
      if (error.name === 'AbortError' || id !== requestId) return;
      indexCache.clear(); sectionCache.clear();
      const copy = labels(), grid = document.getElementById('ProductGridContainer');
      const failure = document.createElement('div'); failure.className = 'ebike-rider-fit__failure'; failure.setAttribute('role', 'alert');
      const message = document.createElement('p'); message.textContent = copy.load_error;
      failure.append(message, makeButton(copy.retry, 'data-rider-retry', 'btn btn--primary'), makeButton(copy.clear, 'data-rider-clear-results', 'btn--link'));
      grid?.replaceChildren(failure); grid?.removeAttribute('aria-busy');
      const productCount = document.getElementById('ProductCount'); if (productCount) productCount.textContent = copy.results_unavailable;
      form.afterRenderSection(); syncWidgets();
    }).finally(() => {
      if (id !== requestId) return;
      activeController = null; activeRequestKey = '';
      showLoading(false);
    });
    return true;
  }

  function showUnit(widget, unit, height) {
    const cmGroup = widget.querySelector('[data-rider-cm-group]'), ftGroup = widget.querySelector('[data-rider-ft-group]');
    widget.querySelector('[data-rider-unit]').value = unit;
    cmGroup.hidden = unit !== 'cm'; ftGroup.hidden = unit !== 'ft';
    const cm = widget.querySelector('[data-rider-cm]'), feet = widget.querySelector('[data-rider-feet]'), inches = widget.querySelector('[data-rider-inches]');
    cm.value = height === null ? '' : height;
    const totalInches = height === null ? null : Math.round(height / 2.54);
    feet.value = totalInches === null ? '' : Math.floor(totalInches / 12); inches.value = totalInches === null ? '' : totalInches % 12;
    const label = widget.querySelector('.ebike-rider-fit__label-row > label'); label.htmlFor = unit === 'cm' ? cm.id : feet.id;
  }

  function syncWidgets() {
    const url = new URL(location.href), current = state(url); preferredUnit = current.unit;
    const appliedType = url.searchParams.getAll('filter.p.m.custom.bike_type').join(',');
    document.documentElement.classList.toggle('ebike-rider-fit-active', Boolean(current.height));
    widgets().forEach(widget => {
      widget.querySelector('[data-rider-height-state]').value = current.height || '';
      widget.querySelector('[data-rider-unit-state]').value = current.height ? current.unit : '';
      widget.querySelector('[data-rider-unknown-state]').value = current.height && current.unknown ? '1' : '';
      const typeState = widget.querySelector('[data-rider-type-state]');
      if (typeState) typeState.value = appliedType;
      if (!dirtyWidgets.has(widget)) {
        showUnit(widget, current.unit, current.height);
        widget.querySelector('[data-rider-unknown]').checked = current.unknown;
        const type = widget.querySelector('[data-rider-type]'), gallery = widget.dataset?.riderGalleryUrl;
        if (type && gallery) type.value = appliedType || (new URL(gallery, url).pathname === url.pathname ? '' : widget.dataset.riderCategoryType);
        widget.querySelector('[data-rider-error]').hidden = true;
      }
      widget.querySelector('[data-rider-unknown-option]').hidden = !current.height || unknownCount === 0;
    });
  }

  function readWidget(widget) {
    const unit = widget.querySelector('[data-rider-unit]').value;
    if (unit === 'ft') {
      const feet = widget.querySelector('[data-rider-feet]').value, inches = widget.querySelector('[data-rider-inches]').value;
      if (!feet && !inches) return { height: null, unit, valid: true };
      const height = imperialHeight(feet, inches);
      return { height, unit, valid: height !== null };
    }
    const value = widget.querySelector('[data-rider-cm]').value, height = canonicalHeight(value);
    return { height, unit, valid: value === '' || height !== null };
  }

  function applyWidget(widget, event) {
    const next = readWidget(widget), form = formFor(widget);
    if (!next.valid) {
      const message = widget.querySelector('[data-rider-error]'); message.textContent = labels().invalid_height; message.hidden = false; return;
    }
    dirtyWidgets.delete(widget);
    preferredUnit = next.unit;
    if (next.height) savePreference({ ...next, unknown: widget.querySelector('[data-rider-unknown]').checked });
    else clearPreference();
    widget.querySelector('[data-rider-height-state]').value = next.height || '';
    widget.querySelector('[data-rider-unit-state]').value = next.height ? next.unit : '';
    widget.querySelector('[data-rider-unknown-state]').value = next.height && widget.querySelector('[data-rider-unknown]').checked ? '1' : '';
    let url = form.buildUrl(); url.searchParams.delete(PAGE); url.searchParams.delete('page');
    const type = widget.querySelector('[data-rider-type]'), galleryUrl = widget.dataset?.riderGalleryUrl;
    if (type && galleryUrl) {
      url = bikeTypeTarget(url, galleryUrl, type.value, widget.dataset.riderCategoryType);
      if (url.pathname !== location.pathname) {
        activeController?.abort(); activeController = null; activeRequestKey = ''; requestId++;
        showLoading(true);
        window.location.assign(url.toString());
        return;
      }
      url.searchParams.set('section_id', form.dataset.sectionId);
    }
    const drawer = widget.closest('#FacetDrawer');
    const showResults = drawer && (event?.key === 'Enter' || event?.target?.closest('[data-rider-apply],#FacetDrawer .drawer__sticky button[aria-controls="FacetDrawer"]'));
    form.renderSection(url.toString(), event);
    if (showResults) drawer.hide();
  }

  function clearFit(event, heightOnly = false, all = false) {
    widgets().forEach(widget => dirtyWidgets.delete(widget));
    clearPreference();
    const form = formFor(event?.target), url = new URL(location.href);
    [HEIGHT, UNIT, UNKNOWN, PAGE, 'page'].forEach(key => url.searchParams.delete(key));
    if (!heightOnly) url.searchParams.delete('filter.p.m.custom.bike_type');
    if (all) for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.set('section_id', form.dataset.sectionId); form.renderSection(url.toString(), event);
  }

  function preserveUrl(url, clearAll = false) {
    if (clearAll) {
      widgets().forEach(widget => dirtyWidgets.delete(widget));
      clearPreference();
      [HEIGHT, UNIT, UNKNOWN].forEach(key => url.searchParams.delete(key));
    } else {
      const current = new URL(location.href);
      [HEIGHT, UNIT, UNKNOWN].forEach(key => { if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key)); });
    }
    url.searchParams.delete(PAGE); url.searchParams.delete('page');
    return url;
  }

  document.addEventListener('click', event => {
    if (!root()) return;
    const drawerApply = event.target.closest('#FacetDrawer .drawer__sticky button[aria-controls="FacetDrawer"]');
    if (drawerApply) {
      const widget = document.querySelector('#DrawerFacetFiltersContainer [data-rider-fit]');
      if (widget) {
        event.preventDefault(); event.stopPropagation();
        applyWidget(widget, event);
      }
      return;
    }
    const target = event.target.closest('[data-rider-apply],[data-rider-clear],[data-rider-clear-height],[data-rider-clear-all],[data-rider-toggle-unknown],[data-rider-page],[data-rider-retry],[data-rider-clear-results]');
    if (!target || (target.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button))) return;
    event.preventDefault(); event.stopPropagation();
    if (target.hasAttribute('data-rider-apply')) applyWidget(target.closest('[data-rider-fit]'), event);
    else if (target.hasAttribute('data-rider-clear-height')) clearFit(event, true);
    else if (target.hasAttribute('data-rider-clear-all')) clearFit(event, false, true);
    else if (target.hasAttribute('data-rider-clear') || target.hasAttribute('data-rider-clear-results')) clearFit(event);
    else {
      const url = new URL(target.hasAttribute('data-rider-page') ? target.href : location.href);
      if (target.hasAttribute('data-rider-toggle-unknown')) {
        url.searchParams.get(UNKNOWN) === '1' ? url.searchParams.delete(UNKNOWN) : url.searchParams.set(UNKNOWN, '1'); url.searchParams.delete(PAGE);
      }
      const form = formFor(target); url.searchParams.set('section_id', form.dataset.sectionId); form.renderSection(url.toString(), event);
    }
  }, true);

  document.addEventListener('change', event => {
    const widget = event.target.closest('[data-rider-fit]'); if (!widget) return;
    dirtyWidgets.add(widget);
    if (event.target.matches('[data-rider-unit]')) {
      event.stopPropagation();
      const priorUnit = widget.querySelector('[data-rider-cm-group]').hidden ? 'ft' : 'cm';
      const prior = priorUnit === 'ft' ? imperialHeight(widget.querySelector('[data-rider-feet]').value, widget.querySelector('[data-rider-inches]').value) : canonicalHeight(widget.querySelector('[data-rider-cm]').value);
      preferredUnit = event.target.value; showUnit(widget, preferredUnit, prior);
      const current = state();
      savePreference({ ...current, unit: preferredUnit });
      widget.querySelector('[data-rider-unit-state]').value = current.height ? preferredUnit : '';
    } else if (event.target.matches('[data-rider-type],[data-rider-unknown]')) {
      event.stopPropagation();
    } else if (event.target.matches('[data-rider-cm],[data-rider-feet],[data-rider-inches]')) event.stopPropagation();
  }, true);

  document.addEventListener('input', event => {
    const widget = event.target.closest('[data-rider-fit]');
    if (widget && event.target.matches('[data-rider-cm],[data-rider-feet],[data-rider-inches]')) dirtyWidgets.add(widget);
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || !event.target.matches('[data-rider-cm],[data-rider-feet],[data-rider-inches]')) return;
    event.preventDefault(); event.stopPropagation(); applyWidget(event.target.closest('[data-rider-fit]'), event);
  }, true);

  function boot(event) {
    if (!root()) return;
    if (event?.type === 'popstate') widgets().forEach(widget => dirtyWidgets.delete(widget));
    const form = formFor(), url = new URL(location.href), saved = readPreference();
    if (saved && !url.searchParams.has(HEIGHT)) {
      preferredUnit = saved.unit;
      if (saved.height && typeof form?.updateURLHash === 'function') {
        url.searchParams.set(HEIGHT, saved.height);
        url.searchParams.set(UNIT, saved.unit);
        if (saved.unknown) url.searchParams.set(UNKNOWN, '1'); else url.searchParams.delete(UNKNOWN);
        url.searchParams.delete(PAGE); url.searchParams.delete('page');
        form.updateURLHash(url);
      }
    }
    syncWidgets();
    const current = state();
    if (current.height) savePreference(current);
    if (current.height && typeof form?.renderSection === 'function') {
      const target = new URL(location.href); target.searchParams.set('section_id', form.dataset.sectionId); form.renderSection(target.toString());
    }
  }

  window.EbikeBuyerRiderFit = { render, preserveUrl };
  document.addEventListener('collection:rerendered', syncWidgets);
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('popstate', boot);
  if (document.readyState === 'complete') boot(); else document.addEventListener('DOMContentLoaded', boot, { once: true });
}

if (typeof document !== 'undefined') installRiderFit();
